import { Separator } from '@/components/ui/separator'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from './ui/sheet'
import { sanity } from '../lib/sanity'

const SITE_SETTINGS_QUERY = `*[_type == "siteSettings"][0]{
  mainMenu[]{
    label,
    path,
    active
  }
}`

const FALLBACK_MENU_ITEMS = [
  { label: 'Home', path: '/' },
  { label: 'Programma', path: '/#programma' },
  { label: 'Over ons', path: '/over-ons' },
  { label: 'Videos', path: '/videos' },
  { label: 'Toespraken', path: '/toespraken' },
  { label: 'Wat herdenken wij?', path: '/wat-herdenken-wij' },
  { label: 'Onderzoek', path: '/onderzoek' },
  { label: 'Contact', path: '/contact' },
]

function Header() {
  const navigate = useNavigate()
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [menuItems, setMenuItems] = useState(FALLBACK_MENU_ITEMS)

  useEffect(() => {
    sanity
      .fetch(SITE_SETTINGS_QUERY)
      .then((settings) => {
        const cmsMenuItems = settings?.mainMenu?.filter(
          (item) => item?.active !== false && item?.label && item?.path
        )

        if (cmsMenuItems?.length) {
          setMenuItems(cmsMenuItems)
        }
      })
      .catch((error) => {
        console.error('Menu kon niet geladen worden:', error)
      })
  }, [])

  function goToProgramma() {
    setIsSheetOpen(false)
    navigate('/#programma')

    setTimeout(() => {
      const section = document.getElementById('programma')
      section?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }, 100)
  }

  function handleMenuItemClick(item) {
    if (item.path === '/#programma') {
      goToProgramma()
      return
    }

    setIsSheetOpen(false)

    if (item.path.startsWith('http')) {
      window.open(item.path, '_blank', 'noopener,noreferrer')
      return
    }

    navigate(item.path)
  }

  return (
    <header
      className={`sticky top-0 z-30 grid w-full grid-cols-[1fr_auto_1fr] items-start bg-[rgb(var(--background))] p-8 text-[rgb(var(--foreground))] ${
        isSheetOpen ? 'pointer-events-none opacity-100' : ''
      }`}
    >
      <div className="flex items-start justify-start">
        <Sheet
          open={isSheetOpen}
          onOpenChange={(open) => {
            setIsSheetOpen(open)
          }}
        >
          <SheetTrigger asChild>
          <button
  type="button"
  aria-label="Open menu"
  className="
    flex items-center justify-center overflow-visible
    bg-transparent p-0
    transition duration-120 hover:opacity-60
    outline-none ring-0 ring-offset-0
    focus:outline-none focus:ring-0 focus:ring-offset-0
    focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0
    active:outline-none
  "
>
  <img
    src="/assets/icons/menu_icon.svg"
    alt="Menu"
    className="w-7 p-0 md:w-7"
  />
</button>
          </SheetTrigger>

          <SheetContent
            side="left"
            className="flex h-auto w-auto flex-col rounded-none border-none bg-white text-[rgb(var(--card-foreground))]"
          >
            <SheetHeader>
              <SheetTitle className="flex items-center font-sans text-xl">
                <span className="mb-2 text-xl text-[#8b8bff]">Menu</span>
              </SheetTitle>

              <Separator className="my-4 border-[1px] border-[#8b8bff] bg-[#ddd]" />
            </SheetHeader>

            <div className="flex-1 overflow-y-auto">
              <div className="my-4">
                <ul className="space-y-4">
                  {menuItems.map((item, index) => (
                    <li key={`${item.path}-${index}`}>
                      <button
                        type="button"
                        onClick={() => handleMenuItemClick(item)}
                        className="bg-transparent p-0 px-0 font-sans text-3xl font-regular text-[#4d4dff] hover:text-[#6666ff] focus:outline-none focus:ring-0 focus-visible:outline-none"
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <SheetFooter className="p-0" />
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex items-center justify-center overflow-visible">
        <Link to="/">
          <img
            src="/assets/logos/HV_Wordmark_Purple.svg"
            alt="Herdenken en Vieren"
            className="h-16 shrink-0 px-0 sm:h-16"
          />
        </Link>
      </div>

      <div className="flex items-start justify-end space-x-4 font-sans focus:outline-none focus:ring-0 focus-visible:outline-none">
      <button
  type="button"
  onClick={goToProgramma}
  aria-label="Zoek in programma"
  className="
    bg-transparent p-0 text-[#5757fd]
    transition duration-120 hover:opacity-60
    outline-none ring-0 ring-offset-0
    focus:outline-none focus:ring-0 focus:ring-offset-0
    focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0
    active:outline-none
  "
>
  <Search className="h-7 w-7" />
</button>
      </div>
    </header>
  )
}

export default Header